import { useState } from "react";
import { CategoryIcon } from "@/components/icons";
import { cn } from "@/lib/utils";

/**
 * The manual card cover image (FS Phase 4, ADR-017) — the manual's **category** icon served from the private object
 * store via a short-lived **presigned URL** (`manual.categoryImageUrl`; category icon → realm standard icon, resolved
 * server-side). Falls back to a neutral **placeholder** (muted surface + the category glyph) when there's no image or it
 * fails to load / expires. The image is decorative (the name + category label carry the meaning) → empty `alt`. Tokens
 * only, so it reads in both light and dark.
 */
export function ManualCardImage({ src, className }: { src: string | null; className?: string }) {
  // Track WHICH url failed, not a boolean — a presigned url rotates (TTL) and a refetch reuses this same instance
  // (cards are keyed by manual id) with a FRESH valid url; a boolean would stay stuck on the placeholder (panel).
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = src != null && src !== "" && src !== failedSrc;
  // Layout-agnostic: the caller sets the aspect/size (a bleeding banner on the grid card, a square thumb in search).
  return (
    <div className={cn("bg-muted overflow-hidden rounded-md", className)}>
      {showImage ? (
        // Category images are small transparent-PNG ICONS → contain (show the whole glyph, never crop) with a little
        // inset, not cover. The transparent areas let the muted surface through (reads in both themes).
        <img src={src} alt="" loading="lazy" className="size-full object-contain p-2" onError={() => setFailedSrc(src)} />
      ) : (
        <div className="text-muted-foreground/40 flex size-full items-center justify-center">
          <CategoryIcon aria-hidden="true" className="size-6" />
        </div>
      )}
    </div>
  );
}
